import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Phone, Mail, Linkedin, Scale, TrendingUp, Briefcase, Gavel, Building2, ClipboardCheck } from "lucide-react";

const LOGO = "https://tozzieassociati.it/wp-content/uploads/2021/10/Tozzi-e-Associati-logo.png";

const services = [
  { icon: Scale, title: "Consulenza Fiscale e Contenzioso Tributario",
    desc: "Tax Planning, assistenza fiscale e tributaria su tre livelli per imprese nazionali e internazionali." },
  { icon: TrendingUp, title: "Ristrutturazione Aziendale e Crisi d'impresa",
    desc: "Consulenza alle società in crisi economica e finanziaria per individuare misure di recupero e risanamento." },
  { icon: Briefcase, title: "Diritto Societario e Commerciale",
    desc: "Consulenza nella costituzione, conduzione e gestione delle società." },
  { icon: Gavel, title: "Contenzioso Civile, Commerciale e Arbitrale",
    desc: "Assistenza avanti l'Autorità Giudiziaria in molteplici procedure giurisdizionali." },
  { icon: Building2, title: "Operazioni Straordinarie",
    desc: "Consulenza integrata legale e fiscale per la massima efficienza economica." },
  { icon: ClipboardCheck, title: "Auditing e Organizzazione Aziendale",
    desc: "Definizione e analisi delle criticità di organizzazioni, sistemi, processi e progetti." },
];

const team = [
  { name: "Dott. Angelo Tozzi", role: "Managing Partner", img: "https://tozzieassociati.it/wp-content/uploads/2021/11/Tozzi_MG_8523-Modifica-scaled.jpg" },
  { name: "Dott. Luigi Maffei", role: "Partner", img: "https://tozzieassociati.it/wp-content/uploads/2021/11/Maffei3_MG_8527-scaled.jpg" },
  { name: "Avv. Laura R. M. Morinello", role: "Partner", img: "https://tozzieassociati.it/wp-content/uploads/2021/11/Morinello_MG_8537-scaled.jpg" },
  { name: "Dott. Luca Coletta Grandolini", role: "Partner", img: "https://tozzieassociati.it/wp-content/uploads/2022/07/Luca-Coletta.jpg" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top bar */}
      <header className="fixed top-0 inset-x-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3">
            <img src={LOGO} alt="Tozzi e Associati" className="h-12 w-auto" />
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-700">
            <a href="#studio" className="hover:text-amber-600 transition-colors">Studio</a>
            <a href="#servizi" className="hover:text-amber-600 transition-colors">Servizi</a>
            <a href="#team" className="hover:text-amber-600 transition-colors">Team</a>
            <a href="#contatti" className="hover:text-amber-600 transition-colors">Contatti</a>
          </nav>
          <Link to="/login">
            <Button className="bg-navy-900 hover:bg-navy-800 text-white h-10 px-5 rounded-sm" data-testid="hero-login-btn">
              Accedi alla piattaforma <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative min-h-[88vh] flex items-center pt-20 bg-gradient-to-br from-navy-900 via-navy-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://tozzieassociati.it/wp-content/uploads/2022/05/Sala-Toro-7_MG_8462-1.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 text-xs uppercase tracking-widest">
              Boutique professionale · Roma
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-tight">
              Costruiamo insieme<br/>
              <span className="text-amber-400">il successo</span> del tuo business.
            </h1>
            <p className="text-lg text-slate-300 max-w-xl leading-relaxed">
              Studio multidisciplinare a Roma con oltre 25 anni di esperienza: avvocati, commercialisti e top management al servizio di medie e grandi imprese nazionali e internazionali.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/login">
                <Button className="bg-amber-500 hover:bg-amber-400 text-navy-900 h-12 px-7 text-sm font-semibold rounded-sm" data-testid="hero-cta-btn">
                  Accedi alla piattaforma <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#servizi">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-7 text-sm rounded-sm bg-transparent">
                  Scopri i servizi
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-8 pt-6 text-sm text-slate-400">
              <div><div className="text-2xl font-heading font-bold text-white">25+</div>anni di esperienza</div>
              <div><div className="text-2xl font-heading font-bold text-white">15+</div>professionisti</div>
              <div><div className="text-2xl font-heading font-bold text-white">6</div>aree di pratica</div>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-amber-500/30 to-transparent blur-3xl" />
              <img src="https://tozzieassociati.it/wp-content/uploads/2022/05/business-people-shaking-hands-together-1-scaled.jpg" alt="Studio" className="relative rounded-sm shadow-2xl border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Studio */}
      <section id="studio" className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-widest text-amber-600 mb-4">Lo Studio</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold text-navy-900 leading-tight mb-8">
            Una boutique multidisciplinare<br/>al servizio dell'impresa.
          </h2>
          <div className="grid md:grid-cols-2 gap-10 text-slate-700 leading-relaxed">
            <p>
              Tozzi e Associati è una realtà boutique multidisciplinare con oltre 25 anni di esperienza, composta da avvocati e commercialisti, specializzata nell'assistenza alle imprese. Il contesto giovane e dinamico ma dal forte know-how nei settori commerciale/societario, M&A e Restructuring, fiscale e tributario e corporate finance, riesce con successo a creare il business insieme all'impresa.
            </p>
            <p>
              Grazie alla sinergia tra le diverse professionalità, lo Studio si distingue per un'assistenza integrata, un vero e proprio <strong className="text-navy-900">one stop shop</strong>. I nostri professionisti lavorano in stretta collaborazione disponendo di tutte le competenze necessarie per progettare, sviluppare e valorizzare al meglio le iniziative imprenditoriali del cliente.
            </p>
          </div>
        </div>
      </section>

      {/* Servizi */}
      <section id="servizi" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-widest text-amber-600 mb-4">Aree di pratica</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-navy-900 leading-tight">I nostri servizi</h2>
            <p className="text-slate-600 mt-4">Sei aree integrate di consulenza per accompagnare l'impresa in ogni fase del proprio percorso.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div key={i} className="group p-7 border border-slate-200 hover:border-amber-400 transition-all duration-300 bg-white hover:shadow-xl rounded-sm" data-testid={`service-${i}`}>
                <div className="w-12 h-12 rounded-sm bg-navy-900 group-hover:bg-amber-500 transition-colors flex items-center justify-center mb-5">
                  <s.icon className="w-6 h-6 text-amber-400 group-hover:text-navy-900 transition-colors" />
                </div>
                <h3 className="font-heading font-bold text-lg text-navy-900 leading-snug mb-3">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="py-24 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-widest text-amber-400 mb-4">Professionisti</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">Il nostro team</h2>
            <p className="text-slate-300 mt-4">Avvocati, commercialisti e manager esperti, uniti da un approccio integrato e personalizzato.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <div key={i} className="group" data-testid={`team-${i}`}>
                <div className="aspect-[3/4] overflow-hidden rounded-sm bg-slate-800">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                </div>
                <div className="mt-4">
                  <div className="font-heading font-bold">{m.name}</div>
                  <div className="text-sm text-amber-400">{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contatti */}
      <section id="contatti" className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-600 mb-4">Contatti</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-navy-900 leading-tight mb-6">Parliamo del tuo progetto.</h2>
            <p className="text-slate-600 leading-relaxed">Siamo a disposizione per valutare insieme come supportare lo sviluppo della tua impresa.</p>
          </div>
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-navy-900 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-amber-400" /></div>
              <div><div className="font-semibold text-navy-900">Sede</div><div className="text-sm text-slate-600">Piazza Cavour, 17 · Scala B, II Piano · Interno 5<br/>00193 Roma, Italia</div></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-navy-900 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-amber-400" /></div>
              <div><div className="font-semibold text-navy-900">Telefono</div><a href="tel:+390645444810" className="text-sm text-slate-600 hover:text-amber-600">+39 0645 444810</a></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-navy-900 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-amber-400" /></div>
              <div>
                <div className="font-semibold text-navy-900">Email</div>
                <a href="mailto:segreteria@tozzieassociati.it" className="block text-sm text-slate-600 hover:text-amber-600">segreteria@tozzieassociati.it</a>
                <a href="mailto:tozzieassociati@legalmail.it" className="block text-sm text-slate-600 hover:text-amber-600">PEC: tozzieassociati@legalmail.it</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-navy-900 flex items-center justify-center flex-shrink-0"><Linkedin className="w-5 h-5 text-amber-400" /></div>
              <div><a href="https://www.linkedin.com/company/35662967" target="_blank" rel="noreferrer" className="text-sm text-slate-600 hover:text-amber-600">LinkedIn · Tozzi e Associati</a></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA piattaforma */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold leading-tight">Accedi alla piattaforma <span className="text-amber-400">Studio Manager</span></h2>
          <p className="text-slate-300 mt-5 max-w-2xl mx-auto">Pratiche, documenti, contatti, email e assistente AI: tutto in un'unica piattaforma sicura riservata ai professionisti dello Studio.</p>
          <Link to="/login">
            <Button className="mt-8 bg-amber-500 hover:bg-amber-400 text-navy-900 h-12 px-8 font-semibold rounded-sm" data-testid="bottom-cta-btn">
              Accedi ora <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-900 border-t border-white/10 text-slate-400 text-xs">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>© 2026 Tozzi e Associati · P.Iva e C.F. 17703861009 · REA RM-1736035</div>
          <div>Studio Manager · v1.0</div>
        </div>
      </footer>
    </div>
  );
}
